import React from'react';
import {
 PieChart as RechartsPieChart,
 Pie,
 Cell,
 Tooltip,
 ResponsiveContainer,
} from'recharts';
import ExportButton from'../ExportButton';

const COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#f97316','#14b8a6','#a855f7','#eab308','#0ea5e9'];
const OTHER_COLOR ='#9ca3af';

export default function PieChartComponent({
 data,
 dataKey ='value',
 nameKey ='name',
 title,
 height = 300,
 innerRadius = 60,
 outerRadius = 100,
 exportFilename,
 onSliceClick,
 // Slices beyond this count are grouped into a single "Other" slice so the
 // donut and legend stay readable when there are many categories.
 maxSlices = 8,
}) {
 // ── Group small slices into "Other" ────────────────────────────────
 // A donut with 20+ categories is unreadable — the legend overwhelms the
 // chart. Keep the top `maxSlices` by value and roll the rest into "Other".
 const { slices, total } = React.useMemo(() => {
 const rows = (Array.isArray(data) ? data : [])
 .map((d) => ({ ...d, __value: Number(d[dataKey]) || 0 }))
 .sort((a, b) => b.__value - a.__value);
 const sum = rows.reduce((s, r) => s + r.__value, 0);
 if (rows.length <= maxSlices) return { slices: rows, total: sum };
 const head = rows.slice(0, maxSlices - 1);
 const tail = rows.slice(maxSlices - 1);
 const otherValue = tail.reduce((s, r) => s + r.__value, 0);
 const other = {
 [nameKey]:`Other (${tail.length})`,
 [dataKey]: otherValue,
 __value: otherValue,
 __isOther: true,
 };
 return { slices: [...head, other], total: sum };
 }, [data, dataKey, nameKey, maxSlices]);

 const colorFor = (entry, idx) =>
 entry.__isOther ? OTHER_COLOR : COLORS[idx % COLORS.length];

 const handleClick = (entry) => {
 if (onSliceClick && entry && !entry.__isOther) onSliceClick(entry);
 };

 // Render percent labels inside slices, but only when the slice is large
 // enough to comfortably fit text. This avoids overlapping labels around
 // the perimeter (the legend already shows the slice names).
 const renderInsideLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
 if (!percent || percent < 0.06) return null;
 const RAD = Math.PI / 180;
 const r = innerRadius + (outerRadius - innerRadius) * 0.55;
 const x = cx + r * Math.cos(-midAngle * RAD);
 const y = cy + r * Math.sin(-midAngle * RAD);
 return (
 <text
 x={x}
 y={y}
 fill="#fff"
 textAnchor="middle"
 dominantBaseline="central"
 fontSize={11}
 fontWeight={600}
 style={{ pointerEvents:'none' }}
 >
 {`${(percent * 100).toFixed(0)}%`}
 </text>
 );
 };

 return (
 <div className="chart-container">
 <div className="flex items-center justify-between mb-4">
 {title && <h3 className="text-sm font-semibold text-gray-700">{title}</h3>}
 {exportFilename && <ExportButton data={data} filename={exportFilename} />}
 </div>      {/* Donut on top, compact scrollable legend below. Stacked so it reads
          well even inside a narrow grid cell (the donut keeps its full width
          instead of being squeezed next to the legend). */}
      <div className="flex flex-col gap-3">
        <ResponsiveContainer width="100%" height={height}>
          <RechartsPieChart>
            <Pie
              data={slices}
              dataKey={dataKey}
              nameKey={nameKey}
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              paddingAngle={2}
              label={renderInsideLabel}
              labelLine={false}
              isAnimationActive={false}
              onClick={onSliceClick ? handleClick : undefined}
              cursor={onSliceClick ?'pointer' : undefined}
            >
              {slices.map((entry, idx) => (
                <Cell key={idx} fill={colorFor(entry, idx)} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [Number(value).toLocaleString(), name]}
              contentStyle={{
                backgroundColor:'var(--tooltip-bg, #fff)',
                color:'var(--tooltip-fg, #111827)',
                border:'1px solid var(--tooltip-border, #e5e7eb)',
                borderRadius:'8px',
                fontSize:'12px',
              }}
              labelStyle={{ color:'var(--tooltip-fg, #111827)' }}
              itemStyle={{ color:'var(--tooltip-fg, #111827)' }}
            />
          </RechartsPieChart>
        </ResponsiveContainer>

        {/* Custom legend: two-column grid on wider cards, single column when
            narrow. Bounded height so a long model list scrolls instead of
            pushing the layout. */}
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 overflow-y-auto pr-1"
          style={{ maxHeight: 140 }}
        >
          {slices.map((entry, idx) => {
            const value = entry.__value;
            const pct = total > 0 ? (value / total) * 100 : 0;
            const clickable = onSliceClick && !entry.__isOther;
            return (
              <li
                key={idx}
                onClick={clickable ? () => handleClick(entry) : undefined}
                className={
                 'flex items-center gap-2 text-xs rounded-md px-1.5 py-1' +
                  (clickable ?' cursor-pointer hover:bg-gray-50' :'')
                }
                title={`${entry[nameKey]}: ${value.toLocaleString()} (${pct.toFixed(1)}%)`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: colorFor(entry, idx) }}
                />
                <span className="truncate flex-1 text-gray-700">{entry[nameKey]}</span>
                <span className="tabular-nums text-gray-500 shrink-0">{pct.toFixed(1)}%</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
